import { ButtonBase, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import type { CampaignLeaksPlatform } from 'src/context/api/campaignLeaks/api';
import { setCampaignLeaksPlatform } from 'src/context/reducers/filter-settings/filterSlice';
import type { RootState } from 'src/context/store';
import { useCampaignLeaksT } from './i18n';

export const PLATFORMS: { value: CampaignLeaksPlatform; label: string; loaded: string }[] = [
  { value: 'google_ads', label: 'Google Ads', loaded: 'Google' },
  { value: 'bing_ads', label: 'Bing Ads', loaded: 'Bing' },
];

/**
 * Platforms of the workspace, from the header's platform list (`loadedPlatform`). Both while that list is
 * not loaded yet, so the page keeps its Google Ads default.
 */
export const availablePlatforms = (loaded: string[] | undefined) => {
  const available = PLATFORMS.filter((platform) => loaded?.includes(platform.loaded));
  return available.length ? available : PLATFORMS;
};

const useAvailablePlatforms = () =>
  availablePlatforms(useSelector((state: RootState) => state.filters.loadedPlatform));

export const platformLabel = (platform: CampaignLeaksPlatform) =>
  PLATFORMS.find((p) => p.value === platform)?.label ?? platform;

/**
 * Platform chosen on the page, shared by both tabs (reset to Google Ads on workspace change).
 * A workspace with a single platform always gets that one.
 */
export const useCampaignLeaksPlatform = (): CampaignLeaksPlatform => {
  const chosen = useSelector((state: RootState) => state.filters.campaignLeaksPlatform) ?? 'google_ads';
  const available = useAvailablePlatforms();
  return available.length === 1 ? available[0].value : chosen;
};

/** Google Ads / Bing Ads switch of the table headers: only when the workspace has both platforms. */
export default function PlatformToggle() {
  const dispatch = useDispatch();
  const platform = useCampaignLeaksPlatform();
  const available = useAvailablePlatforms();
  const ct = useCampaignLeaksT();
  if (available.length < 2) return null;
  return (
    <Stack
      direction="row"
      spacing={1}
      role="group"
      aria-label={ct('toolbar.platform')}
      sx={{ p: '3px', border: '1px solid rgba(145, 158, 171, 0.2)', borderRadius: '8px' }}
    >
      {PLATFORMS.map(({ value, label }) => {
        const selected = value === platform;
        return (
          <ButtonBase
            key={value}
            aria-pressed={selected}
            onClick={() => dispatch(setCampaignLeaksPlatform(value))}
            sx={{
              px: 1,
              borderRadius: '6px',
              bgcolor: selected ? 'rgba(145, 158, 171, 0.2)' : 'transparent',
              '&.Mui-focusVisible': { outline: '2px solid #333C47', outlineOffset: '1px' },
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: '23px', color: '#333C47', whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}
